/**
 * Date Range Picker Core Logic
 * Main class with event handlers and range application
 */
(function(window){
  'use strict';

  // Helper function to get translations
  function translate(key) {
    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
      const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
      const translation = window.TranslationRegistry.translate(key, lang);
      if (translation !== key) return translation;
    }
    if (window.LanguageManager && typeof window.LanguageManager.translate === 'function') {
      const translation = window.LanguageManager.translate(key);
      if (translation !== key) return translation;
    }
    // Fallback to English defaults
    const defaults = {
      'drp_select_date_range': 'Select date range',
      'drp_clear': 'Clear',
      'drp_date_placeholder': 'DD/MM/YYYY',
      'drp_from_date': 'From date',
      'drp_to_date': 'To date',
      'drp_to': 'to',
      'drp_from_label': 'from',
      'drp_to_label': 'to',
      'drp_decades': 'Decades'
    };
    return defaults[key] || key;
  }

  class LiteDateRangePickerCore {
    constructor(opts){
      this.opts = opts||{};
      this.$el=null;
      this.lp=null;
      this.minDate=null;
      this.maxDate=null;
      this.exactOpen=false;
      this.sel={ from:{decade:null,year:null,month:null,day:null,viewDec:null}, to:{decade:null,year:null,month:null,day:null,viewDec:null} };
      this.restoredDate={ from:null, to:null };
      this.currentStage={ from:'decade', to:'decade' };
      this.ui = null;
    }

    init(){
      const el = document.querySelector(this.opts.selector || '#date-range');
      if(!el){ if (window.HyperLog) { window.HyperLog.warn('[LiteDRP] #date-range not found'); } return false; }
      this.$el = el;
      this.ui = new window.LiteDRPUI(this);
      this._refreshBoundsFromMeta(false);
      window.LiteDRPUtils.ensureLitepickerLoaded(() => this._instantiate());
      
        // Listen for metadata/files loaded events to refresh bounds
      try {
        const refreshBoundsWhenReady = () => {
          const meta = window.__idbMetaCache || (window.filesCacheManager && typeof window.filesCacheManager.getMeta==='function' ? window.filesCacheManager.getMeta() : null);
          if (meta && meta.date_min && meta.date_max) {
            const oldMin = this.minDate;
            const oldMax = this.maxDate;
            // Metadata is available, refresh bounds and re-render UI
            this._refreshBoundsFromMeta(true);
            // If bounds changed, force re-render of exact panels to update decade grids
            const boundsChanged = (+oldMin != +this.minDate) || (+oldMax != +this.maxDate);
            if (boundsChanged && this.ui && this.$el) {
              try {
                // Force re-render of exact panels to update decade grids with new bounds
                // This ensures decade buttons (1930s, 1940s, etc.) are shown correctly
                // Even if dropdown is closed, this prepares the UI for when it opens
                this._renderExact('from');
                this._renderExact('to');
              } catch(e) {
                if (window.HyperLog) {
                  try { window.HyperLog.warn('[LiteDRP] Failed to re-render UI after bounds update', { error: e?.message }); } catch(_){ }
                }
              }
            }
          }
        };
        
        // Try immediately in case metadata is already loaded
        setTimeout(refreshBoundsWhenReady, 100);
        
        // Listen for filesLoaded event
        document.addEventListener('filesLoaded', refreshBoundsWhenReady);
        
        // Also check periodically for a short time after init (fallback)
        let checkCount = 0;
        const checkInterval = setInterval(() => {
          checkCount++;
          refreshBoundsWhenReady();
          if (checkCount >= 20) { // Check for up to 2 seconds (20 * 100ms) to ensure metadata is loaded
            clearInterval(checkInterval);
          }
        }, 100);
      } catch(_){}
      
      try {
        const toggle = document.getElementById('date-range-toggle');
        if (toggle) {
          toggle.addEventListener('shown.bs.dropdown', () => {
            this._refreshBoundsFromMeta(true);
            try {
              const fHidden = document.querySelector('.filter-date-from')?.value || '';
              const tHidden = document.querySelector('.filter-date-to')?.value || '';
              if (!fHidden && !tHidden) { 
                // CRITICAL: Reset selection and ensure decade view is shown when no dates are set
                this._resetSelectionToDefault();
                // Clear restoredDate to prevent showing day view
                this.restoredDate.from = null;
                this.restoredDate.to = null;
                // Force re-render to show decade view
                this._renderExact('from');
                this._renderExact('to');
              }
            } catch(_){ }
          });
          try {
            const menu = toggle.nextElementSibling;
            if (menu) {
              toggle.addEventListener('hide.bs.dropdown', (ev) => {
                if (window.__keepDateRangeOpen) { ev.preventDefault(); }
              });
              const setFlag = () => { window.__keepDateRangeOpen = true; setTimeout(()=>{ window.__keepDateRangeOpen=false; }, 0); };
              ['click','mousedown','mouseup','touchstart'].forEach(evt => { 
                try { 
                  const opts = evt === 'touchstart' ? { passive: true } : false;
                  menu.addEventListener(evt, setFlag, opts); 
                } catch(_){} 
              });
            }
          } catch(_){}
        }
      } catch(_){ }
      try {
        const clearBtn = document.querySelector('.filters-clear');
        if (clearBtn) { clearBtn.addEventListener('click', () => { try { this.applyRange(null, null, 'ui'); } catch(_){ } }); }
      } catch(_){ }
      return true;
    }

    _instantiate(){
      try {
        const hidden = document.createElement('input');
        hidden.type = 'text';
        hidden.className = 'd-none';
        this.$el.appendChild(hidden);
        this.lp = null;
        this._renderToolbar();
      } catch(e) {
        if (window.HyperLog) { window.HyperLog.warn('[LiteDRP] init failed', { error: e?.message }); }
      }
    }

    _emitRange(from, to, source){
      // Clamp dates to bounds if they exist
      let fClamped = from || '';
      let tClamped = to || '';
      if (fClamped && this.minDate) {
        const fDate = window.LiteDRPUtils.parseYMD(fClamped);
        if (fDate && fDate < this.minDate) fClamped = window.LiteDRPUtils.formatYMD(this.minDate);
      }
      if (fClamped && this.maxDate) {
        const fDate = window.LiteDRPUtils.parseYMD(fClamped);
        if (fDate && fDate > this.maxDate) fClamped = window.LiteDRPUtils.formatYMD(this.maxDate);
      }
      if (tClamped && this.minDate) {
        const tDate = window.LiteDRPUtils.parseYMD(tClamped);
        if (tDate && tDate < this.minDate) tClamped = window.LiteDRPUtils.formatYMD(this.minDate);
      }
      if (tClamped && this.maxDate) {
        const tDate = window.LiteDRPUtils.parseYMD(tClamped);
        if (tDate && tDate > this.maxDate) tClamped = window.LiteDRPUtils.formatYMD(this.maxDate);
      }
      try { const $from = document.querySelector('.filter-date-from'); if ($from) $from.value = fClamped; } catch(_){ }
      try { const $to = document.querySelector('.filter-date-to'); if ($to) $to.value = tClamped; } catch(_){ }
      try { window.dispatchEvent(new CustomEvent('dateRangeChanged', { detail: { from: fClamped, to: tClamped, source: source||'api' } })); } catch(_){ }
      // Don't update URL here - URL will be updated when search is executed (debounce)
      try { if (window.filterManager && typeof window.filterManager.applyFilters==='function') window.filterManager.applyFilters(false); } catch(_){ }
      try { this._syncInputsFromHidden(); } catch(_){ }
    }

    applyRange(from, to, src){
      // Ensure bounds are loaded before validation
      if (!this.minDate || !this.maxDate) {
        this._refreshBoundsFromMeta(false);
      }
      const f = (from instanceof Date) ? window.LiteDRPUtils.formatYMD(from) : '';
      const t = (to instanceof Date) ? window.LiteDRPUtils.formatYMD(to) : '';
      if (src === 'restore') {
        // CRITICAL: Clear restoredDate first to prevent showing wrong view when dates are empty
        this.restoredDate.from = null;
        this.restoredDate.to = null;
        // Clamp dates to bounds
        if (from instanceof Date) {
          if (this.minDate && from < this.minDate) from = this.minDate;
          if (this.maxDate && from > this.maxDate) from = this.maxDate;
          this.restoredDate.from = from;
        }
        if (to instanceof Date) {
          if (this.minDate && to < this.minDate) to = this.minDate;
          if (this.maxDate && to > this.maxDate) to = this.maxDate;
          this.restoredDate.to = to;
        }
      }
      if (src !== 'restore') {
        this._emitRange(f, t, src||'api');
      }
      if (src === 'restore') {
        const isStartOfDecade = (d) => {
          if (!d) return false;
          const year = d.getFullYear();
          const decadeStart = Math.floor(year / 10) * 10;
          return year === decadeStart && d.getMonth() === 0 && d.getDate() === 1;
        };
        const isEndOfDecade = (d) => {
          if (!d) return false;
          const year = d.getFullYear();
          const decadeEnd = Math.floor(year / 10) * 10 + 9;
          return year === decadeEnd && d.getMonth() === 11 && d.getDate() === 31;
        };
        const isFirstDayOfYear = (d) => d && d.getMonth() === 0 && d.getDate() === 1;
        const isLastDayOfYear = (d) => d && d.getMonth() === 11 && d.getDate() === 31;
        const isFirstDayOfMonth = (d) => d && d.getDate() === 1;
        const isLastDayOfMonth = (d) => {
          if (!d) return false;
          const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
          return d.getDate() === lastDayOfMonth;
        };
        if (this.restoredDate.from) {
          const from = this.restoredDate.from;
          this.sel.from.decade = Math.floor(from.getFullYear() / 10) * 10;
          this.sel.from.year = from.getFullYear();
          this.sel.from.viewDec = this.sel.from.decade; // Set viewDec for year grid display
          if (isStartOfDecade(from)) {
            this.sel.from.month = null;
            this.sel.from.day = null;
            this.currentStage.from = 'decade';
          } else if (isFirstDayOfYear(from)) {
            this.sel.from.month = null;
            this.sel.from.day = null;
            this.currentStage.from = 'year';
          } else if (isFirstDayOfMonth(from)) {
            this.sel.from.month = from.getMonth() + 1;
            this.sel.from.day = null;
            this.currentStage.from = 'month';
          } else {
            this.sel.from.month = from.getMonth() + 1;
            this.sel.from.day = from.getDate();
            this.currentStage.from = 'day';
          }
        } else {
          this.sel.from.decade = null;
          this.sel.from.year = null;
          this.sel.from.month = null;
          this.sel.from.day = null;
          this.currentStage.from = 'decade';
        }
        if (this.restoredDate.to) {
          const to = this.restoredDate.to;
          this.sel.to.decade = Math.floor(to.getFullYear() / 10) * 10;
          this.sel.to.year = to.getFullYear();
          this.sel.to.viewDec = this.sel.to.decade; // Set viewDec for year grid display
          if (isEndOfDecade(to)) {
            this.sel.to.month = null;
            this.sel.to.day = null;
            this.currentStage.to = 'decade';
          } else if (isLastDayOfYear(to)) {
            this.sel.to.month = null;
            this.sel.to.day = null;
            this.currentStage.to = 'year';
          } else if (isLastDayOfMonth(to)) {
            this.sel.to.month = to.getMonth() + 1;
            this.sel.to.day = null;
            this.currentStage.to = 'month';
          } else {
            this.sel.to.month = to.getMonth() + 1;
            this.sel.to.day = to.getDate();
            this.currentStage.to = 'day';
          }
        } else {
          this.sel.to.decade = null;
          this.sel.to.year = null;
          this.sel.to.month = null;
          this.sel.to.day = null;
          this.currentStage.to = 'decade';
        }
        this._renderExact('from');
        this._renderExact('to');
        // Emit clamped dates after UI processing
        const fClamped = this.restoredDate.from ? window.LiteDRPUtils.formatYMD(this.restoredDate.from) : '';
        const tClamped = this.restoredDate.to ? window.LiteDRPUtils.formatYMD(this.restoredDate.to) : '';
        this._emitRange(fClamped, tClamped, 'restore');
      }
      try {
        if (this.lp) {
          if (!f && !t) {
            if (typeof this.lp.clearSelection === 'function') this.lp.clearSelection();
            else if (typeof this.lp.setDateRange === 'function') this.lp.setDateRange(null, null, true);
          } else if (typeof this.lp.setDateRange === 'function') {
            this.lp.setDateRange(from, to, true);
          }
        }
      } catch(_){ }
    }

    _syncInputsFromHidden(){
      const ymdToDmy = (s)=>{ try { if(!s || s.includes('NaN')) return ''; const [y,m,d]=s.split('-'); if (!y || !m || !d || y==='NaN' || m==='NaN' || d==='NaN') return ''; return `${d}/${m}/${y}`; } catch(_) { return ''; } };
      try {
        const fHidden = document.querySelector('.filter-date-from')?.value || '';
        const tHidden = document.querySelector('.filter-date-to')?.value || '';
        const inpF = this.$el.querySelector('.drp-input-from'); const inpT = this.$el.querySelector('.drp-input-to');
        if (inpF) inpF.value = ymdToDmy(fHidden);
        if (inpT) inpT.value = ymdToDmy(tHidden);
      } catch(_){ }
    }

    _refreshBoundsFromMeta(updatePicker){
      try {
        const meta = window.__idbMetaCache || (window.filesCacheManager && typeof window.filesCacheManager.getMeta==='function' ? window.filesCacheManager.getMeta() : null);
        const minStr = this.opts.minDate || (meta && meta.date_min) || null;
        const maxStr = this.opts.maxDate || (meta && meta.date_max) || null;
        const newMin = window.LiteDRPUtils.parseYMD(minStr);
        const newMax = window.LiteDRPUtils.parseYMD(maxStr);
        const changed = (+newMin != +this.minDate) || (+newMax != +this.maxDate);
        if (changed) {
          this.minDate = newMin; this.maxDate = newMax;
          if (updatePicker && this.lp && typeof this.lp.setOptions==='function') {
            this.lp.setOptions({ minDate: this.minDate || undefined, maxDate: this.maxDate || undefined });
          }
          // Re-render UI components to reflect new bounds
          if (updatePicker && this.ui && this.$el) {
            try {
              // Force re-render of exact panels to update decade grids with new bounds
              // This ensures decade buttons (1930s, 1940s, etc.) are shown correctly
              this._renderExact('from');
              this._renderExact('to');
            } catch(e) {
              if (window.HyperLog) {
                try { window.HyperLog.warn('[LiteDRP] Failed to re-render UI after bounds update', { error: e?.message }); } catch(_){ }
              }
            }
          }
          // Debug logging disabled to reduce console noise
          // if (window.HyperLog) {
          //   try { window.HyperLog.debug('[LiteDRP] Bounds', { min: minStr, max: maxStr }); } catch(_){ }
          // }
        }
      } catch(_){ }
    }

    _renderToolbar(){
      try {
        const old = this.$el.querySelector('.drp-toolbar');
        if (old && old.parentNode) old.parentNode.removeChild(old);
        const toolbar = document.createElement('div');
        toolbar.className = 'drp-toolbar d-flex flex-wrap align-items-center justify-content-between mb-2';
        const btn = (cls, label) => { const b=document.createElement('button'); b.type='button'; b.className=cls; b.textContent=label; return b; };
        const clearBtn = btn('btn btn-md btn-outline-secondary', translate('drp_clear'));
        clearBtn.onclick = (ev) => { ev.preventDefault(); ev.stopPropagation(); this.applyRange(null, null, 'preset'); this._resetSelectionToDefault(true); this._renderExact('from'); this._renderExact('to'); };
        const title = document.createElement('div'); title.className='text-muted'; title.textContent = translate('drp_select_date_range');
        toolbar.appendChild(title); toolbar.appendChild(clearBtn);
        const placeholder = translate('drp_date_placeholder');
        const fromLabel = translate('drp_from_date');
        const toLabel = translate('drp_to_date');
        const fromText = translate('drp_from_label');
        const toText = translate('drp_to_label');
        
        const exactPane = document.createElement('div'); exactPane.className='drp-exact mt-2';
        exactPane.innerHTML = this.ui.buildExactPaneHtml(fromText, toText, placeholder, fromLabel, toLabel);
        this.$el.prepend(exactPane);
        this.$el.prepend(toolbar);
        try {
          const fHidden = document.querySelector('.filter-date-from')?.value || '';
          const tHidden = document.querySelector('.filter-date-to')?.value || '';
          if (!fHidden && !tHidden) { this._resetSelectionToDefault(); }
          this._renderExact('from');
          this._renderExact('to');
          this._wireManualInputs();
          this._syncInputsFromHidden();
        } catch(_){ }
      } catch(_){ }
    }

    _wireManualInputs(){
      const parse = (s) => { if (!s) return null; const m=s.trim().match(/^([0-3]?\d)\/(0?\d|1[0-2])\/(\d{4})$/); if (!m) return null; const d=parseInt(m[1],10), mo=parseInt(m[2],10), y=parseInt(m[3],10); const dt=new Date(y,mo-1,d); if (isNaN(dt)) return null; return dt; };
      const inpF = this.$el.querySelector('.drp-input-from');
      const inpT = this.$el.querySelector('.drp-input-to');
      const sync = () => {
        const f = parse(inpF && inpF.value); const t = parse(inpT && inpT.value);
        if (f || t) this.applyRange(f||this._currentFromDate(), t||this._currentToDate(), 'manual');
      };
      if (inpF) inpF.onchange = (e)=>{ e.stopPropagation(); sync(); };
      if (inpT) inpT.onchange = (e)=>{ e.stopPropagation(); sync(); };
    }

    _renderExact(which){
      const panel = this.$el.querySelector(`.drp-panel[data-which="${which}"]`);
      if (!panel) return;
      const stageDec = panel.querySelector('.drp-stage-decade');
      const stageYear = panel.querySelector('.drp-stage-year');
      const stageMonth = panel.querySelector('.drp-stage-month');
      const stageDay = panel.querySelector('.drp-stage-day');
      const s = this.sel[which] || (this.sel[which] = { decade:null, year:null, month:null, day:null, viewDec:null });
      const current = this.currentStage[which] || 'decade';
      const showDec = (current === 'decade');
      const showYear = (current === 'year');
      const showMonth = (current === 'month');
      const showDay = (current === 'day');
      if (showDec) {
        stageDec.innerHTML = this.ui.buildDecadeGrid(which);
      }
      if (showYear) {
        if (!s.viewDec) {
          const baseY = s.decade != null ? s.decade : (this.minDate ? this.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear());
          s.viewDec = window.LiteDRPUtils.roundDownDecade(baseY);
        }
        if (!s.year && which === 'from') {
          const currentDate = this._currentFromDate();
          if (currentDate) {
            s.year = currentDate.getFullYear();
          } else {
            // Use default year if no date is set
            s.year = this.minDate ? this.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear();
          }
        }
        stageYear.innerHTML = this.ui.buildYearGrid(which, s.viewDec);
      }
      if (showMonth) {
        if (!s.year) {
          const currentDate = which === 'from' ? this._currentFromDate() : this._currentToDate();
          if (currentDate) {
            s.year = currentDate.getFullYear();
          } else {
            // Use default year if no date is set
            s.year = this.minDate ? this.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear();
          }
        }
        stageMonth.innerHTML = this.ui.buildMonthGrid(which, s.year);
      }
      if (showDay) {
        if (!s.year || !s.month) {
          const currentDate = which === 'from' ? this._currentFromDate() : this._currentToDate();
          if (currentDate) {
            s.year = currentDate.getFullYear();
            s.month = currentDate.getMonth() + 1;
          } else {
            // CRITICAL: If no date is set and we're showing day view, reset to decade view
            // Day view should only show when a date is selected or restored
            // This prevents showing January 1950 when picker opens empty
            this.currentStage[which] = 'decade';
            // Re-render with correct stage (decade view)
            return this._renderExact(which);
          }
        }
        stageDay.innerHTML = this.ui.buildDayGrid(which, s.year, s.month);
      }
      stageDec.classList.toggle('d-none', !showDec);
      stageYear.classList.toggle('d-none', !showYear);
      stageMonth.classList.toggle('d-none', !showMonth);
      stageDay.classList.toggle('d-none', !showDay);
      if (!panel.__handlersAttached) {
        panel.__handlersAttached = true;
        panel.addEventListener('click', (ev) => {
          let target = ev.target;
          let el = (target && target.nodeType === 1) ? target : (target && (target.parentElement || target.parentNode));
          ev.stopPropagation();
          ev.preventDefault();
          const btnDec = el && el.closest ? el.closest('.drp-decade-item') : null;
          if (btnDec) { this._onDecade(which, parseInt(btnDec.getAttribute('data-y'), 10)); return; }
          const btnYear = el && el.closest ? el.closest('.drp-year-item') : null;
          if (btnYear) { this._onYear(which, parseInt(btnYear.getAttribute('data-y'), 10)); return; }
          const btnMonth = el && el.closest ? el.closest('.drp-month-item') : null;
          if (btnMonth) { this._onMonth(which, parseInt(btnMonth.getAttribute('data-m'), 10)); return; }
          const btnDay = el && el.closest ? el.closest('.drp-day-item') : null;
          if (btnDay) { this._onDay(which, parseInt(btnDay.getAttribute('data-d'), 10)); return; }
          if (el && el.closest && el.closest('.drp-nav-decade-left')) { this._navDecade(which, -10); return; }
          if (el && el.closest && el.closest('.drp-nav-decade-right')) { this._navDecade(which, +10); return; }
          if (el && el.closest && el.closest('.drp-nav-year-left')) { this._navYear(which, -1); return; }
          if (el && el.closest && el.closest('.drp-nav-year-right')) { this._navYear(which, +1); return; }
          if (el && el.closest && el.closest('.drp-nav-month-left')) { 
            const s=this.sel[which]; 
            if (s && s.month) { 
              let newMonth = s.month - 1;
              let newYear = s.year;
              if (newMonth < 1) { newMonth = 12; newYear = (newYear || window.LiteDRPUtils.getGlobalMinYear()) - 1; }
              s.month = newMonth;
              s.year = newYear;
              this._persistSel();
              this._renderExact(which);
            } 
            return; 
          }
          if (el && el.closest && el.closest('.drp-nav-month-right')) { 
            const s=this.sel[which]; 
            if (s && s.month) { 
              let newMonth = s.month + 1;
              let newYear = s.year;
              if (newMonth > 12) { newMonth = 1; newYear = (newYear || window.LiteDRPUtils.getGlobalMinYear()) + 1; }
              s.month = newMonth;
              s.year = newYear;
              this._persistSel();
              this._renderExact(which);
            } 
            return; 
          }
          if (el && el.closest && el.closest('.drp-back-decade')) {
            this.currentStage[which] = 'decade';
            this._persistSel(); 
            this._renderExact(which); 
            return;
          }
          if (el && el.closest && el.closest('.drp-back-year')) {
            this.currentStage[which] = 'year';
            this._persistSel(); 
            this._renderExact(which); 
            return;
          }
          if (el && el.closest && el.closest('.drp-back-month')) {
            this.currentStage[which] = 'month';
            this._persistSel(); 
            this._renderExact(which); 
            return;
          }
        });
      }
    }

    _onDecade(which, decadeStart){
      const s = this.sel[which]; 
      s.decade = decadeStart; 
      s.year = null;
      s.month = null;
      s.day = null;
      this._persistSel();
      s.viewDec = decadeStart;
      this.currentStage[which] = 'year';
      this.restoredDate.from = null;
      this.restoredDate.to = null;
      const currentDate = which === 'from' ? this._currentFromDate() : this._currentToDate();
      const decadeEnd = decadeStart + 9;
      const currentYear = currentDate ? currentDate.getFullYear() : null;
      const dateInDecade = currentYear && currentYear >= decadeStart && currentYear <= decadeEnd;
      if (!dateInDecade) {
        if (which==='from') { this.applyRange(new Date(decadeStart,0,1), this._currentToDate(), 'exact'); }
        else { this.applyRange(this._currentFromDate(), new Date(decadeStart+9,11,31), 'exact'); }
      }
      this._renderExact('from'); this._renderExact('to');
    }

    _onYear(which, year){
      const s = this.sel[which]; 
      if (!s.decade) {
        s.decade = window.LiteDRPUtils.roundDownDecade(year);
      }
      s.year = year; 
      s.month = null;
      s.day = null;
      this._persistSel();
      this.currentStage[which] = 'month';
      this.restoredDate.from = null;
      this.restoredDate.to = null;
      const currentDate = which === 'from' ? this._currentFromDate() : this._currentToDate();
      const currentYear = currentDate ? currentDate.getFullYear() : null;
      const dateInYear = currentYear === year;
      if (!dateInYear) {
        if (which==='from') { this.applyRange(new Date(year,0,1), this._currentToDate(), 'exact'); } 
        else { this.applyRange(this._currentFromDate(), new Date(year,11,31), 'exact'); }
      }
      this._renderExact('from'); this._renderExact('to');
    }

    _onMonth(which, month){
      const s = this.sel[which]; 
      if (!s.decade && s.year) {
        s.decade = window.LiteDRPUtils.roundDownDecade(s.year);
      }
      if (!s.year && s.decade) {
        s.year = s.decade;
      }
      s.month = month; 
      s.day = null;
      this._persistSel();
      this.currentStage[which] = 'day';
      this.restoredDate.from = null;
      this.restoredDate.to = null;
      const currentDate = which === 'from' ? this._currentFromDate() : this._currentToDate();
      const currentYear = currentDate ? currentDate.getFullYear() : null;
      const currentMonth = currentDate ? currentDate.getMonth() + 1 : null;
      const dateInMonth = currentYear === s.year && currentMonth === month;
      if (!dateInMonth) {
        const defaultYear = window.LiteDRPUtils.getGlobalMinYear();
        if (which==='from') { this.applyRange(new Date(s.year||s.decade||defaultYear,(month-1),1), this._currentToDate(), 'exact'); }
        else { const y = s.year||((s.decade||defaultYear)+9); const last=new Date(y, month, 0).getDate(); this.applyRange(this._currentFromDate(), new Date(y,(month-1), last), 'exact'); }
      }
      this._renderExact('from'); this._renderExact('to');
    }

    _onDay(which, day){
      const s = this.sel[which]; s.day = day; this._persistSel();
      this.restoredDate.from = null;
      this.restoredDate.to = null;
      const defaultYear = window.LiteDRPUtils.getGlobalMinYear();
      if (which==='from') { const y=s.year||s.decade||defaultYear; const m=(s.month||1)-1; this.applyRange(new Date(y,m,day), this._currentToDate(), 'exact'); }
      else { const y = s.year||((s.decade||defaultYear)+9); const m=(s.month||12)-1; this.applyRange(this._currentFromDate(), new Date(y,m,day), 'exact'); }
      this._renderExact('from'); this._renderExact('to');
    }

    _persistSel(){ window.LiteDRPUtils.persistSel(this.sel); }
    
    _loadSel(){ this.sel = window.LiteDRPUtils.loadSel(); }

    _navDecade(which, delta){
      const s = this.sel[which];
      const c = this._getConstraints(which);
      const minY = c.min ? c.min.getFullYear() : (this.minDate ? this.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear());
      const maxY = c.max ? c.max.getFullYear() : (this.maxDate ? this.maxDate.getFullYear() : (new Date()).getFullYear());
      const startDec = Math.floor(minY/10)*10; const endDec = Math.floor(maxY/10)*10;
      let next = (s.viewDec || startDec) + delta;
      if (next < startDec) next = startDec; if (next > endDec) next = endDec;
      s.viewDec = next; this._persistSel(); this._renderExact(which);
    }

    _navYear(which, delta){
      const s = this.sel[which]; if (!s.year) return;
      const c = this._getConstraints(which);
      const minY = c.min ? c.min.getFullYear() : (this.minDate ? this.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear());
      const maxY = c.max ? c.max.getFullYear() : (this.maxDate ? this.maxDate.getFullYear() : (new Date()).getFullYear());
      let y = s.year + delta; if (y < minY) y = minY; if (y > maxY) y = maxY; s.year = y; this._persistSel(); this._renderExact(which);
    }

    _resetSelectionToDefault(clearPersist){
      const defaultYear = this.minDate ? this.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear();
      const startDec = Math.floor(defaultYear/10)*10;
      this.sel = { from:{decade:null,year:null,month:null,day:null,viewDec:startDec}, to:{decade:null,year:null,month:null,day:null,viewDec:startDec} };
      this.currentStage = { from:'decade', to:'decade' };
      if (clearPersist) { try { localStorage.removeItem('lite_drp_sel'); } catch(_){ } }
    }

    _getConstraints(which){
      return window.LiteDRPUtils.getConstraints(which, this.minDate, this.maxDate);
    }

    _currentFromDate(){ return window.LiteDRPUtils.currentFromDate(); }
    
    _currentToDate(){ return window.LiteDRPUtils.currentToDate(); }
  }

  window.LiteDateRangePickerCore = LiteDateRangePickerCore;
})(window);

